import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getUserMentions } from 'services/dashboard.service';
import useMentionsState from 'state/useMentionsState';
import { filterGroupedMentionsByChannel } from 'utils/dashboardMentions.utils';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';

interface IUseDashboardMentionsParams {
  isCustomerChat: boolean;
}

/**
 * Carga las menciones del dashboard, las persiste en el estado global y devuelve
 * el subconjunto filtrado según el canal (operaciones o chat con clientes).
 */
const useDashboardMentions = ({ isCustomerChat }: IUseDashboardMentionsParams) => {
  const {
    pendingViewingMentions,
    pendingResolutionMentions,
    resolvedMentions,
    isColumnsLoading,
    setIsColumnsLoading,
    updateAllMentions
  } = useMentionsState();

  const hasLoadedRef = useRef(false);

  const filteredMentions = useMemo(
    () =>
      filterGroupedMentionsByChannel(
        { pendingViewingMentions, pendingResolutionMentions, resolvedMentions },
        { isCustomerChat }
      ),
    [pendingViewingMentions, pendingResolutionMentions, resolvedMentions, isCustomerChat]
  );

  const obtainMentions = useCallback(async (showLoader = false) => {
    try {
      if (showLoader) setIsColumnsLoading(true);

      const response = await getUserMentions();

      updateAllMentions({
        pendingViewingMentions: response.pendingViewingMentions,
        pendingResolutionMentions: response.pendingResolutionMentions,
        resolvedMentions: response.resolvedMentions
      });
    } catch {
      displayErrorNotification();
    } finally {
      if (showLoader) setIsColumnsLoading(false);
    }
  }, [setIsColumnsLoading, updateAllMentions]);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      obtainMentions(true);
    }
  }, [obtainMentions]);

  return { filteredMentions, isColumnsLoading, obtainMentions };
};

export default useDashboardMentions;
